import React, { ChangeEvent, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import CompanyTagSelect from './CompanyTagSelect';
import { Option } from 'react-bootstrap-typeahead/types/types';
import { CompanyResource, defaultCompanyResource } from '../../common/entities';
import EcosystemNameSelect from './EcosystemNameSelect';
import { CompaniesDataService } from '../../common/services';
import RequiredIcon from '../../common/components/RequiredIcon';
import RoundTypeSelect from './RoundTypeSelect';

interface Props {
  onSave: (company: CompanyResource) => void;
}

/**
 * @description Form to edit/create a company.
 * @param {Props} props
 * @return {JSX.Element}
 */
const CompanyForm = (props: Props): JSX.Element => {
  const [data, setData] = useState<CompanyResource>(defaultCompanyResource());
  const [validated, setValidated] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const onChangeSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const newData = { ...data };
    newData.attributes[event.target.name] = event.target.value;
    setData(newData);
    console.log(newData.attributes);
  };

  const onTypeaheadSelectMultiple = (name: string, selected: Option[]) => {
    const newData = { ...data };
    const flatSelected = selected.map((item) => {
      if (typeof item === 'object') {
        return item.label;
      } else {
        return item;
      }
    });
    newData.attributes[name] = flatSelected as string[];
    setData(newData);
  };

  const onTypeaheadSelectSingle = (name: string, selected: Option[]) => {
    const newData = { ...data };

    if (!selected.length) {
      newData.attributes[name] = '';
      setData(newData);
      return;
    }

    const firstSelected = selected[0];
    newData.attributes[name] =
      typeof firstSelected === 'string' ? firstSelected : firstSelected.label;
    setData(newData);
  };

  const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const newData = { ...data };

    if (
      event.target.maxLength > -1 &&
      event.target.value.length > event.target.maxLength
    ) {
      return;
    }
    newData.attributes[event.target.name] = event.target.value;

    setData(newData);
  };

  const onFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    console.log('onSubmi');
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    const valid = form.checkValidity();
    // console.log(
    //   (document.getElementById('primary-tag') as HTMLInputElement).validity,
    // );

    console.log(data);
    // props.onSave(data);
    // mark the form as having its validity checked
    setValidated(true);
    //
    if (!valid || !data.attributes.primaryTag) {
      console.log('not valid');
      return;
    }
    //
    // setBusy(true);
    // setErrorMessage('');
    setErrorMessage(undefined);
    try {
      const newCompany = await CompaniesDataService.create(data);
      props.onSave(newCompany);
    } catch (err) {
      setErrorMessage('There was an error saving this company.');
      console.error('.', err, data);
    } finally {
      // setBusy(false);
    }
  };

  return (
    <Form noValidate validated={validated} onSubmit={onFormSubmit}>
      <Form.Group className="mb-3" controlId="companyNameInput">
        <Form.Label>
          Company Name <RequiredIcon />
        </Form.Label>
        <Form.Control
          type="text"
          placeholder="ACME Inc."
          value={data.attributes.companyName}
          name={'companyName'}
          required={true}
          onChange={onChangeInput}
        />
        <Form.Control.Feedback type="invalid">
          This field is required.
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="headquartersInput">
        <Form.Label>
          Headquarters <RequiredIcon />
        </Form.Label>
        <Form.Control
          type="text"
          placeholder="Kitchener"
          value={data.attributes.headquarters}
          name={'headquarters'}
          required={true}
          onChange={onChangeInput}
        />
        <Form.Control.Feedback type="invalid">
          This field is required.
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="ecosystemNameInput">
        <Form.Label>
          Ecosystem Name <RequiredIcon />
        </Form.Label>
        <EcosystemNameSelect
          value={data.attributes.ecosystemName}
          name={'ecosystemName'}
          required={true}
          onChange={onChangeSelect}
        />
        <Form.Control.Feedback type="invalid">
          This field is required.
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="dateFoundedInput">
        <Form.Label>
          Year Founded <RequiredIcon />
        </Form.Label>
        <Form.Control
          type="number"
          placeholder=""
          name={'dateFounded'}
          required={true}
          maxLength={4}
          value={data.attributes.dateFounded}
          onChange={onChangeInput}
        />
        <Form.Control.Feedback type="invalid">
          This field is required.
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3" controlId="primaryTagInput">
        <Form.Label>
          Primary Tag <RequiredIcon />
        </Form.Label>
        <CompanyTagSelect
          id={'primary-tag'}
          multiple={false}
          selected={data.attributes.primaryTag}
          name={'primaryTag'}
          required={false}
          primarySecondaryOnly={true}
          onSelect={onTypeaheadSelectSingle}
        />

        <Form.Control.Feedback
          type="invalid"
          style={{
            display:
              validated && !data.attributes.primaryTag ? 'block' : 'none',
          }}
        >
          This field is required.
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="secondaryTagInput">
        <Form.Label>Secondary Tag</Form.Label>
        <CompanyTagSelect
          id={'secondary-tag'}
          multiple={false}
          selected={data.attributes.secondaryTag}
          name={'secondaryTag'}
          primarySecondaryOnly={true}
          onSelect={onTypeaheadSelectSingle}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="tagsInput">
        <Form.Label>Other Tags</Form.Label>
        <CompanyTagSelect
          id={'tags'}
          multiple={true}
          selected={data.attributes.tags}
          name={'tags'}
          required={undefined}
          onSelect={onTypeaheadSelectMultiple}
        />
        <p>
          Primary and secondary tags will be automatically prepended to this
          list when submitted.
        </p>
        {/* <Form.Control.Feedback*/}
        {/*  type="invalid"*/}
        {/*  style={{*/}
        {/*    display:*/}
        {/*      validated && data.attributes.tags.length === 0 ? 'block' : 'none',*/}
        {/*  }}*/}
        {/* >*/}
        {/*  This field is required.*/}
        {/* </Form.Control.Feedback>*/}
      </Form.Group>
      <Form.Group className="mb-3" controlId="marketplaceTagsInput">
        <Form.Label>Marketplace Tags</Form.Label>
        <CompanyTagSelect
          id={'marketplace-tags'}
          multiple={true}
          selected={data.attributes.marketplaceTags}
          name={'marketplaceTags'}
          onSelect={onTypeaheadSelectMultiple}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="descriptionInput">
        <Form.Label>
          Tagline (from LinkedIn or Twitter) <RequiredIcon />
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          placeholder=""
          value={data.attributes.description}
          name={'description'}
          required={true}
          onChange={onChangeInput}
        />
        <p>Tagline (description) in one sentence. Two sentences maximum.</p>
        <Form.Control.Feedback type="invalid">
          This field is required.
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="websiteInput">
        <Form.Label>
          Website <RequiredIcon />
        </Form.Label>
        <Form.Control
          type="text"
          placeholder="https://example.com"
          value={data.attributes.website}
          name={'website'}
          required={true}
          onChange={onChangeInput}
        />
        <Form.Control.Feedback type="invalid">
          This field is required.
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="linkedInUrlInput">
        <Form.Label>LinkedIn URL</Form.Label>
        <Form.Control
          type="text"
          placeholder=""
          value={data.attributes.linkedInUrl}
          name={'linkedInUrl'}
          onChange={onChangeInput}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="twitterInput">
        <Form.Label>Twitter</Form.Label>
        <Form.Control
          type="text"
          placeholder=""
          value={data.attributes.twitter}
          name={'twitter'}
          onChange={onChangeInput}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="logoUrlInput">
        <Form.Label>
          Logo URL <RequiredIcon />
        </Form.Label>
        <Form.Control
          type="text"
          placeholder=""
          value={data.attributes.logoUrl}
          name={'logoUrl'}
          required={true}
          onChange={onChangeInput}
        />
        <Form.Control.Feedback type="invalid">
          This field is required.
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="latestRoundDateInput">
        <Form.Label>Latest Round Date</Form.Label>
        <Form.Control
          type="date"
          placeholder=""
          value={data.attributes.latestRoundDate}
          name={'latestRoundDate'}
          onChange={onChangeInput}
        />
      </Form.Group>
      <Form.Group className="mb-3" controlId="latestRoundTypeInput">
        <Form.Label>Latest Round Type</Form.Label>
        <RoundTypeSelect
          onChange={onChangeSelect}
          value={data.attributes.latestRoundType}
          name={'latestRoundType'}
        />
      </Form.Group>
      <Button variant="primary" type="submit">
        Save Company
      </Button>
      {errorMessage ? (
        <Alert variant={'danger'}>
          <Alert.Heading>Error</Alert.Heading>
          <p>{errorMessage}</p>
        </Alert>
      ) : null}
    </Form>
  );
};

export default CompanyForm;
